import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { differenceInSeconds } from 'date-fns';
import { countdownParts } from '../../utils/countdownParts';
import { DesignMode, getDesignTokens } from './designTokens';

type TimerField = 'days' | 'hours' | 'minutes' | 'seconds';
type TimerSize = 'compact' | 'default' | 'large';

export type SiteCountdownTimerProps = {
  targetDate: Date | string;
  fieldsToShow?: TimerField[];
  compact?: boolean;
  size?: TimerSize;
  label?: string;
  mode?: DesignMode;
};

const labels: Record<TimerField, string> = {
  days: 'Days',
  hours: 'Hours',
  minutes: 'Mins',
  seconds: 'Secs'
};

const zeroPad = (value: number) => String(Math.max(0, value)).padStart(2, '0');

export const SiteCountdownTimer = ({
  targetDate,
  fieldsToShow = ['days', 'hours', 'minutes', 'seconds'],
  compact = false,
  size,
  label,
  mode = 'light'
}: SiteCountdownTimerProps) => {
  const tokens = getDesignTokens(mode);
  const resolvedSize = size ?? (compact ? 'compact' : 'default');
  const isCompact = resolvedSize === 'compact';
  const isLarge = resolvedSize === 'large';
  const resolvedTargetDate = useMemo(() => new Date(targetDate), [targetDate]);
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    Math.max(0, differenceInSeconds(resolvedTargetDate, new Date()))
  );

  useEffect(() => {
    const tick = () => {
      setRemainingSeconds(
        Math.max(0, differenceInSeconds(resolvedTargetDate, new Date()))
      );
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [resolvedTargetDate]);

  const duration = countdownParts(remainingSeconds);

  const values: Record<TimerField, number> = {
    days: duration.days ?? 0,
    hours: duration.hours ?? 0,
    minutes: duration.minutes ?? 0,
    seconds: duration.seconds ?? 0
  };

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            mb: isLarge ? { xs: 1.5, md: 2 } : 1,
            color: tokens.colors.textMuted,
            fontFamily: 'var(--design-font-display)',
            fontSize: isLarge
              ? { xs: 18, sm: 22, md: 26 }
              : isCompact
                ? 12
                : 14,
            fontWeight: 800,
            lineHeight: 1.2
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${fieldsToShow.length}, minmax(0, 1fr))`,
          gap: isCompact ? 1 : isLarge ? { xs: 1, sm: 1.5, md: 2 } : 1.5,
          width: '100%'
        }}
      >
        {fieldsToShow.map((field) => (
          <Box
            key={field}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              isolation: 'isolate',
              minWidth: 0,
              minHeight: isCompact
                ? 74
                : isLarge
                  ? { xs: 112, sm: 138, md: 164 }
                  : 96,
              p: isCompact ? 1 : isLarge ? { xs: 1.25, sm: 2, md: 2.5 } : 1.5,
              borderRadius: tokens.radius.lg,
              border: `1px solid ${
                mode === 'light'
                  ? 'rgba(29,29,31,0.08)'
                  : 'rgba(255,255,255,0.16)'
              }`,
              background:
                mode === 'light'
                  ? 'linear-gradient(180deg, #ffffff, #f6f6f8)'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.035))',
              boxShadow:
                mode === 'light'
                  ? 'inset 0 1px 0 rgba(255,255,255,0.96), 0 10px 26px rgba(0,0,0,0.07)'
                  : 'inset 0 1.5px 0 rgba(255,255,255,0.2), inset 0 -1px 0 rgba(0,0,0,0.22), inset -10px -10px 22px rgba(255,255,255,0.08), 0 10px 24px rgba(0,0,0,0.2)',
              backdropFilter: 'blur(12px) saturate(145%)',
              WebkitBackdropFilter: 'blur(12px) saturate(145%)',
              textAlign: 'center',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                zIndex: -1,
                borderRadius: 'inherit',
                background:
                  mode === 'light'
                    ? 'linear-gradient(180deg, rgba(255,255,255,0.54), rgba(255,255,255,0) 62%)'
                    : 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0) 62%)',
                pointerEvents: 'none'
              }
            }}
          >
            <Typography
              sx={{
                color: tokens.colors.text,
                fontSize: isCompact
                  ? { xs: 26, sm: 32 }
                  : isLarge
                    ? { xs: 42, sm: 64, md: 82 }
                    : { xs: 36, sm: 50 },
                lineHeight: 1,
                fontWeight: 850,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {zeroPad(values[field])}
            </Typography>
            <Typography
              sx={{
                mt: isLarge ? { xs: 1, sm: 1.25 } : 0.8,
                color:
                  mode === 'light'
                    ? tokens.colors.tealDeep
                    : tokens.colors.teal,
                fontSize: isCompact
                  ? 11
                  : isLarge
                    ? { xs: 11, sm: 13, md: 15 }
                    : 12,
                lineHeight: 1,
                fontWeight: 900,
                letterSpacing: 0,
                textTransform: 'uppercase'
              }}
            >
              {labels[field]}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
